// auditoria.service.ts
//
// O caminho pelo qual `Avançado › Logs` lê o log de auditoria do sistema.
//
// O QUE MUDOU NO PRODUTO: até o card deste arquivo, NADA do que as pessoas
// fazem no Central era registrado, e a tela de Logs dizia isso por escrito. A
// entidade passou a existir — `alog."Auditoria"` no Postgres —, e `GET
// /auditoria` a expõe paginada.
//
// SOMENTE ADMINISTRADOR. O backend decide isso no `hasPermission`, não o menu:
// quem não é administrador recebe **403**, e isso NÃO É FALHA — é a regra. A
// tela trata esse status em separado, com um texto que explica em vez de um
// "não foi possível carregar" que faria o usuário achar que quebrou.
//
// O LOG NÃO VEM DO InfoToken, de propósito. Foi um `subsidiary.logs.map(...)`
// sobre uma chave que nunca existiu no token que deixava esta tela branca por
// meses. Log paginado não cabe num JWT: ele cresce sem limite e o token vai em
// TODO cabeçalho de TODA requisição. Vem por HTTP, com página e filtro.
//
// O BLOCO `cobertura` É O QUE IMPEDE A TELA DE MENTIR. Com poucos pontos de
// escrita instrumentados, uma tabela vazia se leria como "nada aconteceu no
// sistema". `cobertura` chega do servidor com a lista do que TEM ponto de
// escrita — a tela renderiza essa lista, e ela não pode divergir do backend
// porque é o backend que a produz.

import api from "./api.service";

export interface Paginacao {
  page: number;
  limit: number;
  total: number;
  pages: number;
}

/** Uma linha do log. Todo campo opcional pode chegar nulo — a tela mostra "—". */
export interface EventoDeAuditoria {
  auditoriaUuid: string;
  ocorridoEm: string;
  acao: string;
  entidade: string;
  entidadeUuid: string | null;
  entidadeRotulo: string | null;
  atorUuid: string | null;
  atorNome: string | null;
  atorEmail: string | null;
  atorTipo: string;
  organizationUuid: string | null;
  companyUuid: string | null;
  subsidiaryUuid: string | null;
  rota: string | null;
  metodo: string | null;
  resultado: string;
  antes: any | null;
  depois: any | null;
  detalhe: string | null;
}

/** O catálogo do que É registrado hoje. Vem do servidor, nunca escrito à mão. */
export interface ItemDaCobertura {
  entidade: string;
  rotulo: string;
  acoes: string[];
}

/**
 * Em que estado está a estrutura da trilha no banco.
 *
 * `"ausente"` é a diferença que impede o vazio honesto de virar vazio enganoso:
 * sem ela, "a tabela está vazia" e "a tabela NÃO EXISTE e nada está sendo
 * gravado" chegavam aqui idênticas (`desde: null`), e a tela dizia "o log passou
 * a existir agora" justamente no caso em que a estrutura tinha falhado no boot.
 * São os mesmos três valores que `GET /saude` já usa para o schema `alog`.
 */
export type EstruturaDaTrilha = "ok" | "ausente" | "desconhecida";

export interface RespostaDeAuditoria {
  dados: EventoDeAuditoria[];
  paginacao: Paginacao;
  cobertura: ItemDaCobertura[];
  /** Data da PRIMEIRA linha do log. `null` = a tabela está vazia. */
  desde: string | null;
  estrutura?: EstruturaDaTrilha;
  /**
   * O que o servidor recebeu, não conseguiu usar, e portanto NÃO está refletido
   * na lista. Cada texto é para ser mostrado ao usuário como veio.
   *
   * Existe porque o contrário — descartar calado — é a tela mentindo:
   * `?de=15/08/2026` (o formato que o dono digita) era jogado fora e a lista
   * INTEIRA aparecia como se fosse o período pedido.
   */
  filtrosIgnorados?: string[];
}

export interface FiltroDeAuditoria {
  page?: number;
  limit?: number;
  subsidiary?: string;
  entidade?: string;
  entidadeUuid?: string;
  ator?: string;
  acao?: string;
  /** ISO. Data ilegível é IGNORADA pelo servidor, não vira erro. */
  de?: string;
  ate?: string;
}

/** Só o que foi preenchido vai na query — chave vazia viraria filtro por "". */
function comoQuery(filtro: FiltroDeAuditoria): string {
  const parametros = new URLSearchParams();
  if (filtro.page) parametros.set("page", String(filtro.page));
  if (filtro.limit) parametros.set("limit", String(filtro.limit));
  if (filtro.subsidiary) parametros.set("subsidiary", filtro.subsidiary);
  if (filtro.entidade) parametros.set("entidade", filtro.entidade);
  if (filtro.entidadeUuid) parametros.set("entidadeUuid", filtro.entidadeUuid);
  if (filtro.ator) parametros.set("ator", filtro.ator);
  if (filtro.acao) parametros.set("acao", filtro.acao);
  if (filtro.de) parametros.set("de", filtro.de);
  if (filtro.ate) parametros.set("ate", filtro.ate);
  return parametros.toString();
}

const auditoriaService = {
  // Rota RELATIVA, sem barra inicial: o `baseURL` do api.service já traz o
  // prefixo, e uma barra à frente o descartaria.
  async listar(filtro: FiltroDeAuditoria = {}): Promise<RespostaDeAuditoria> {
    const { data } = await api.get(`auditoria?${comoQuery(filtro)}`);
    return data;
  },

  /** Só o catálogo + desde + total. É a chamada barata para explicar o vazio. */
  async cobertura(): Promise<{
    cobertura: ItemDaCobertura[];
    desde: string | null;
    total: number | null;
    estrutura?: EstruturaDaTrilha;
  }> {
    const { data } = await api.get("auditoria/cobertura");
    return data;
  },
};

/** Os verbos do log em português de tela. Fora da lista, mostra o verbo cru. */
export const ROTULO_DA_ACAO: Record<string, string> = {
  criar: "Criou",
  atualizar: "Editou",
  desligar: "Desligou",
  excluir: "Excluiu",
  aprovar: "Aprovou",
  recusar: "Recusou",
  cancelar: "Cancelou",
  publicar: "Publicou",
  trocarEndereco: "Trocou o endereço",
};

/**
 * O nome de tela do tipo de objeto. `suprimentos:fornecedores` vira
 * "Suprimentos › fornecedores" em vez de aparecer cru.
 */
export function rotuloDaEntidade(entidade: string): string {
  const nomes: Record<string, string> = {
    item: "Item de PDV",
    itemInfo: "Configuração de item",
    loja: "Loja",
    ambiente: "Ambiente",
    ordemDeCompra: "Ordem de compra",
    site: "Site",
  };
  if (nomes[entidade]) return nomes[entidade];
  if (entidade.startsWith("suprimentos:")) {
    return `Suprimentos › ${entidade.slice("suprimentos:".length)}`;
  }
  return entidade;
}

export default auditoriaService;
